import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FALLBACK_SPORTS from "../../../assets/images/sports-banner-image.png";
import { siteApi } from "../../../lib/api";
import { launchProviderLobby } from "../../../lib/gameLaunch";
import {
  getRows,
  getIntegratedSportsbookRoute,
  getVendorImages,
  getVendorTitle,
  matchesKeyword,
} from "../../../utils/gameCatalog";
import { StyledGameCatalogPage } from "../Casino/StyledGameCatalogPage";

const SPORTS_BASE_ITEMS = [
  {
    id: "domestic",
    title: "국내형 스포츠",
    meta: "국내 경기",
    to: "/sports?type=DOMESTIC",
  },
  {
    id: "international",
    title: "해외형 스포츠",
    meta: "해외 경기",
    to: "/sports?type=INTERNATIONAL",
  },
  {
    id: "inplay",
    title: "라이브",
    meta: "라이브 경기",
    to: "/sports?type=LIVE",
  },
  {
    id: "esports",
    title: "E스포츠",
    meta: "E스포츠 경기",
    to: "/sports?type=SPECIAL",
  },
];

const SORT_OPTIONS = [
  { value: "popular", label: "인기" },
  { value: "name", label: "이름순" },
];

const SportsCard = ({ item, onLaunch }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const image = item.image || FALLBACK_SPORTS;
  const content = (
    <>
      <span className="catalog-card-image">
        {image && !imageFailed ? (
          <img src={image} alt={item.title} loading="lazy" onError={() => setImageFailed(true)} />
        ) : (
          <span className="catalog-fallback-title">{item.title}</span>
        )}
        <span className="catalog-card-title">{item.title}</span>
        <span className="catalog-card-overlay">
          <span className="catalog-play-button" aria-hidden="true" />
          <strong>{item.title}</strong>
        </span>
      </span>
      <span className="catalog-card-meta">
        <span className="live-dot" />
        {item.meta}
      </span>
    </>
  );

  if (item.to) {
    return (
      <Link className="catalog-card-link" to={item.to}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className="catalog-card-button" onClick={() => onLaunch(item)}>
      {content}
    </button>
  );
};

const SportsGamesPage = () => {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadSportsVendors = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await siteApi.getGameVendors({
          game_type: "live-sports",
          order_by_name: "sequence",
          order_by_type: "ASC",
          limit: "ALL",
        });

        if (!isMounted) return;

        if (response.success) {
          setVendors(getRows(response));
          return;
        }

        setError(response.error || "스포츠 목록을 불러오지 못했습니다.");
      } catch {
        if (isMounted) setError("스포츠 목록을 불러오지 못했습니다.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadSportsVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  const items = useMemo(() => {
    const vendorItems = vendors.map((vendor) => ({
      id: `vendor:${vendor?.id || vendor?.vendor_name}`,
      title: getVendorTitle(vendor, "스포츠"),
      meta: "스포츠 벤더",
      image: getVendorImages(vendor)[0] || "",
      to: getIntegratedSportsbookRoute(vendor),
      vendor,
    }));

    const rows = [...SPORTS_BASE_ITEMS.map((item) => ({ ...item, image: FALLBACK_SPORTS })), ...vendorItems]
      .filter((item) =>
        matchesKeyword(
          search,
          item.title,
          item.meta,
          item.vendor?.vendor_name,
          item.vendor?.display_name
        )
      );

    if (sortBy === "name") {
      return [...rows].sort((a, b) => a.title.localeCompare(b.title, "ko"));
    }

    return rows;
  }, [search, sortBy, vendors]);

  const handleLaunch = async (item) => {
    if (!item.vendor) return;
    await launchProviderLobby(item.vendor, {
      label: item.title,
      gameType: "live-sports",
      meta: "스포츠",
    });
  };

  return (
    <StyledGameCatalogPage>
      <div className="catalog-shell">
        <label className="catalog-search-box">
          <span className="catalog-search-icon" aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="스포츠 게임 검색"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <div className="catalog-toolbar">
          <div className="catalog-section-title">
            <h1>스포츠게임</h1>
            <span>{isLoading ? "불러오는 중" : `${items.length}개 항목`}</span>
          </div>

          <label className="catalog-sort-control">
            <span>정렬</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <div className="catalog-state error">{error}</div> : null}

        <div className="catalog-grid" aria-busy={isLoading}>
          {isLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                <span className="catalog-skeleton" key={`sports-skeleton-${index}`} />
              ))
            : items.map((item) => (
                <SportsCard key={item.id} item={item} onLaunch={handleLaunch} />
              ))}
        </div>

        {!isLoading && !error && items.length === 0 ? (
          <div className="catalog-state">표시할 스포츠게임이 없습니다.</div>
        ) : null}
      </div>
    </StyledGameCatalogPage>
  );
};

export default SportsGamesPage;
